// =============================================================================
// src/components/shared/BookingSlotPicker.tsx
// =============================================================================

import type { AvailableSlot } from "@/types/booking.types";
import { supabase } from "@cl/supabase";
import { useLangBoundT } from "@h/useLangBoundT";
import { useResponsive } from "@h/useResponsive";
import { cn } from "@u/cn";
import { Calendar } from "@ui/calendar";
import { ArrowLeft, Clock, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { Locale } from "react-day-picker";
import { enUS, es, fr, it, pt } from "react-day-picker/locale";
import { toast } from "sonner";

interface Props {
  ownerId: string;
  /** When set, availability resolves against this .tap venue's settings. */
  timezone: string;
  slotMinutes?: number;
  windowDays?: number;
  /** Called whenever the selected slot changes (null = deselected). */
  onSlotSelect: (slot: AvailableSlot | null) => void;
  selectedSlot: AvailableSlot | null;
  /** Accent color class for selected state. Default: primary. */
  accentClass?: string;
  /** Exclude this booking ID from conflict checks (used when editing). */
  excludeBookingId?: string;
  /** Pre-select this date when the picker opens. */
  initialDate?: Date;
  /**
   * Slots precargados. Con esto el picker NO llama a `get-availability` — lo usa
   * la landing para mostrar el picker real con datos mock (mismo componente que
   * el viewer ⇒ cero drift entre demo y producto).
   */
  slots?: AvailableSlot[];
  /** Idioma explícito (el `.tap` pasa el que eligió el visitante). Omitido =
   *  idioma detectado de la app. */
  lang?: string;
}

/** Locales del calendario (react-day-picker los trae; date-fns por dentro).
 *  Sin entrada → inglés, el default del paquete. */
const DP_LOCALES: Record<string, Locale> = {
  en: enUS,
  es,
  pt,
  fr,
  it,
};

export function BookingSlotPicker({
  ownerId,
  timezone,
  slotMinutes,
  windowDays = 30,
  onSlotSelect,
  selectedSlot,
  accentClass,
  excludeBookingId,
  initialDate,
  slots,
  lang,
}: Props) {
  const { t, dateLocale } = useLangBoundT(lang);
  const { isMobile, isLandscapeMobile } = useResponsive();
  const isTouchDevice = isMobile || isLandscapeMobile;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [slotsByDate, setSlotsByDate] = useState<Record<string, AvailableSlot[]>>({});
  const [loading, setLoading] = useState(slots === undefined);
  const [mobileStep, setMobileStep] = useState<"calendar" | "slots">("calendar");

  const today = new Date();
  // eslint-disable-next-line react-hooks/purity
  const maxDate = new Date(Date.now() + windowDays * 24 * 3600 * 1000);

  const toDateKey = (d: Date) =>
    d.toLocaleDateString("en-CA", { timeZone: timezone });

  const groupByDate = (list: AvailableSlot[]) => {
    const grouped: Record<string, AvailableSlot[]> = {};
    for (const slot of list) {
      const key = toDateKey(new Date(slot.start));
      (grouped[key] ??= []).push(slot);
    }
    return grouped;
  };

  // Slots inyectados (demo/landing): se derivan, sin red y sin estado.
  const byDate = slots ? groupByDate(slots) : slotsByDate;

  useEffect(() => {
    if (slots) return;

    async function fetchSlots() {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("get-availability", {
          body: {
            owner_id: ownerId,
            date_from: toDateKey(today),
            date_to: toDateKey(maxDate),
            timezone,
            ...(excludeBookingId ? { exclude_booking_id: excludeBookingId } : {}),
          },
        });
        if (error) throw error;
        setSlotsByDate(groupByDate(data.available_slots ?? []));
      } catch {
        toast.error(t("error_loading_slots", "Error al cargar horarios disponibles"));
      } finally {
        setLoading(false);
      }
    }
    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, timezone, slots]);

  const availableDates = new Set(Object.keys(byDate));
  const isDateDisabled = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return !availableDates.has(`${y}-${m}-${d}`);
  };

  const selectedDateKey = selectedDate ? toDateKey(selectedDate) : "";
  const slotsForDate = selectedDateKey ? (byDate[selectedDateKey] ?? []) : [];

  // Antes hardcodeaban "es-ES": un visitante inglés veía "jueves, 17 de julio"
  // en el picker aunque el resto del wizard ya seguía su idioma.
  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(dateLocale, {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDate = (d: Date) =>
    d.toLocaleDateString(dateLocale, {
      timeZone: timezone,
      weekday: "long",
      day: "numeric",
      month: "long",
    });

  // Meses/días del calendario en el mismo idioma que el resto (antes siempre
  // en inglés, el default de react-day-picker). `languageOnly`: "pt-BR" → "pt".
  const dpLocale = DP_LOCALES[dateLocale.split("-")[0]] ?? enUS;

  const selected = accentClass ?? "border-primary bg-primary/10 text-primary font-medium";
  const unselected = "border-border/60 bg-muted/20 hover:border-primary/50 hover:bg-primary/5 text-foreground";

  // Every slot lasts the same, so the duration is said once in the heading
  // instead of "/60m" repeated on each button.
  const slotHeading = (
    <p className="flex items-baseline justify-between gap-2 text-sm font-medium text-muted-foreground">
      {t("select_time", "Selecciona un horario")}
      {slotMinutes ? (
        <span className="text-xs font-normal">
          {t("slot_duration_each", { count: slotMinutes })}
        </span>
      ) : null}
    </p>
  );

  const slotGrid = (cols = 3) => (
    <div className={cn("grid gap-2", cols === 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3")}>
      {slotsForDate.map((slot) => (
        <button
          key={slot.start}
          type="button"
          onClick={() => onSlotSelect(selectedSlot?.start === slot.start ? null : slot)}
          className={cn(
            "flex items-center justify-center gap-1.5 rounded-xl border px-2 py-2.5 text-sm transition-all min-h-11",
            selectedSlot?.start === slot.start ? selected : unselected,
          )}
        >
          <Clock
            size={13}
            className={selectedSlot?.start === slot.start ? "text-primary" : "text-muted-foreground"}
          />
          {formatTime(slot.start)}
        </button>
      ))}
    </div>
  );

  const noSlotsMsg = (
    <div className="text-center py-6 bg-muted/20 rounded-2xl border border-border/40">
      <p className="text-sm text-muted-foreground px-4">
        {t("no_slots", "No hay horarios disponibles para este día. Prueba con otra fecha.")}
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Mobile: two-step (calendar → slots) ──────────────────────────────────
  if (isTouchDevice) {
    if (mobileStep === "slots" && selectedDate) {
      return (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => { setMobileStep("calendar"); onSlotSelect(null); }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} />
            <span className="capitalize">{formatDate(selectedDate)}</span>
          </button>

          {slotHeading}

          {slotsForDate.length === 0 ? noSlotsMsg : slotGrid(3)}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => {
            setSelectedDate(d);
            onSlotSelect(null);
            if (d) setMobileStep("slots");
          }}
          disabled={isDateDisabled}
          locale={dpLocale}
          startMonth={today}
          endMonth={maxDate}
          className="rounded-2xl border border-border/40 bg-muted/20 w-full"
        />
      </div>
    );
  }

  // ── Desktop: calendar + inline slot grid ─────────────────────────────────
  return (
    <div className="space-y-4">
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={(d) => { setSelectedDate(d); onSlotSelect(null); }}
        disabled={isDateDisabled}
        locale={dpLocale}
        startMonth={today}
        endMonth={maxDate}
        className="rounded-2xl border border-border/40 bg-muted/20 w-full"
      />

      {selectedDate && (
        <div className="space-y-2">
          {slotHeading}
          {slotsForDate.length === 0 ? noSlotsMsg : (
            <div className="max-h-48 overflow-y-auto pr-1 custom-scrollbar">
              {slotGrid(2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

